import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import { format } from "date-fns";
import MissingInformationTile from "../components/Dashboard/MissingInformationTile";
import UserService from "../services/UserService";
import Dashboard from "../models/Dashboard";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import PendingTimeoffTile from "../components/Dashboard/PendingTimeoffTile";
import RequestedTimeOff from "../components/Dashboard/RequestedTimeOff";
import UpcomingTimeoffsTile from "../components/Dashboard/UpcomingTimeoffsTile";
import MissingWorklogsTile from "../components/Dashboard/MissingWorklogsTile";
import EmptyDashboard from "../components/Dashboard/EmptyDashboard";
import SitesTile from "../components/Dashboard/SitesTile";
import { useSiteList } from "../hooks/useSiteList";
import { KpiTile } from "../components/Dashboard/KpiTile";
import { Token } from "../theme";
import TeamCoverageTile from "../components/Dashboard/TeamCoverageTile";
import { Routes } from "../types/Routes";
import { QuickLogTile } from "../components/Dashboard/QuickLogTile";
import { WorklogsApprovalTile } from "../components/Dashboard/WorklogsApprovalTile";
import { useTranslation } from "react-i18next";
import RoleUtils from "../utils/RoleUtils";
import { KpiTileSkeleton, PanelTileSkeleton } from "../components/Dashboard/DashboardSkeleton";

const KPI_SKELETON_COUNT = 3;
const PANEL_SKELETON_COUNT = 2;

export default function DashboardList() {
  const { t } = useTranslation();
  const history = useHistory();

  const [dashboard, setDashboard] = useState<Dashboard>();
  const { siteActions } = useSiteList();

  useEffect(() => {
    loadContent();
    siteActions.getRecommendedSites(0, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContent = async () => {
    (await UserService.getDashboard()).onSuccess((response) => setDashboard(response)).onError((err) => NotificationService.error(err));
  };

  const areActivitiesToShow = (instance: Dashboard) => {
    return (
      (instance.missingWorklogsPerProject?.length ?? 0) > 0 ||
      (instance.timeoffPendingApproval?.length ?? 0) > 0 ||
      (instance.upcomingTimeoffs?.length ?? 0) > 0 ||
      (instance.missingInformation?.length ?? 0) > 0 ||
      (instance.myTimeoffs?.length ?? 0) > 0 ||
      !!instance.kpis
    );
  };

  const areSitesToShow = () => {
    return siteActions.result !== undefined && siteActions.result > 0;
  };

  const emptyDashboard = () => {
    return dashboard && !areActivitiesToShow(dashboard) && !areSitesToShow();
  };

  const missingDayChips = (dates?: string[]) => (dates ?? []).map((iso) => format(new Date(`${iso}T12:00:00`), "EEE d"));

  const kpis = () => {
    if (!dashboard?.kpis) return [];
    const kpiData = dashboard.kpis;
    const tiles = [];
    const goWorklog = () => {
      const now = new Date();
      history.push(`${Routes.WORKLOG}?year=${now.getFullYear()}&month=${now.getMonth() + 1}`);
    };
    const goTimeOff = () => history.push(Routes.TIME_OFF);
    const goVacationReport = () => history.push(Routes.VACATION_REPORT);
    const goWorklogReport = () => history.push(Routes.WORKLOG_REPORT);

    const expected = kpiData.expectedHoursMonth || 1;
    const progress = Math.round((kpiData.loggedHoursMonth / expected) * 100);
    const monthName = format(new Date(), "MMMM");

    tiles.push(
      <KpiTile
        key="logged"
        label={`${t("Logged in")} ${monthName}`}
        value={kpiData.loggedHoursMonth}
        suffix={`/ ${kpiData.expectedHoursMonth} h`}
        progress={progress}
        icon={<AccessTimeOutlinedIcon />}
        onClick={goWorklog}
      />,
    );

    tiles.push(
      <KpiTile
        key="missing"
        label={t("Missing days")}
        value={kpiData.missingDays}
        suffix={t("days")}
        chips={missingDayChips(kpiData.missingDayDates)}
        icon={<ErrorOutlineIcon />}
        iconColor={Token.statusWarningFg}
        onClick={goWorklog}
      />,
    );

    const vacationDetail =
      kpiData.plannedVacationDays != null && kpiData.plannedVacationMonthIndex != null
        ? `${kpiData.plannedVacationDays} ${t("days planned in")} ${format(
            new Date(new Date().getFullYear(), kpiData.plannedVacationMonthIndex, 1),
            "MMMM",
          )}`
        : undefined;
    tiles.push(
      <KpiTile
        key="vacation"
        label={t("Vacation balance")}
        value={Math.round(kpiData.vacationDaysTaken ?? 0)}
        suffix={t("days taken")}
        detail={vacationDetail}
        icon={<BeachAccessOutlinedIcon />}
        iconColor={Token.magenta}
        onClick={goVacationReport}
      />,
    );

    if (kpiData.pendingApprovals !== undefined && kpiData.pendingApprovals > 0) {
      const approvalDetail =
        kpiData.oldestApprovalDate && kpiData.oldestApprovalDaysAgo !== undefined
          ? `${t("Oldest since")} ${kpiData.oldestApprovalDate} - ${kpiData.oldestApprovalDaysAgo} ${t("days")}`
          : undefined;
      tiles.push(
        <KpiTile
          key="waiting"
          label={t("Waiting on you")}
          value={kpiData.pendingApprovals}
          suffix={t("approvals")}
          detail={approvalDetail}
          icon={<PlaylistAddCheckOutlinedIcon />}
          onClick={() => {
            if (dashboard.timeoffPendingApproval.length > 0) {
              goTimeOff();
            } else {
              goWorklogReport();
            }
          }}
        />,
      );
    }

    if (kpiData.teamOnLeaveCount !== undefined && kpiData.teamOnLeaveCount > 0) {
      tiles.push(
        <KpiTile
          key="team-leave"
          label={t("Team on leave")}
          value={kpiData.teamOnLeaveCount}
          suffix={t("this month")}
          detail={kpiData.teamOnLeaveNames?.join(", ")}
          icon={<BeachAccessOutlinedIcon />}
          iconColor={Token.magenta}
          onClick={goTimeOff}
        />,
      );
    }

    return tiles;
  };

  // Every tile renders null when its rows are empty, so testing the array for truthiness is not
  // enough: an empty array still produced a grid cell and left a hole in the layout.
  const has = (rows?: unknown[]) => !!rows && rows.length > 0;

  const panels = [
    has(dashboard?.missingWorklogsPerProject) && <MissingWorklogsTile rows={dashboard!.missingWorklogsPerProject} />,
    has(dashboard?.myTimeoffs) && <RequestedTimeOff rows={dashboard!.myTimeoffs} />,
    has(dashboard?.missingInformation) && <MissingInformationTile rows={dashboard!.missingInformation} />,
    has(dashboard?.timeoffPendingApproval) && <PendingTimeoffTile rows={dashboard!.timeoffPendingApproval} fetchData={loadContent} />,
    has(dashboard?.upcomingTimeoffs) && <TeamCoverageTile rows={dashboard!.upcomingTimeoffs} />,
    (has(dashboard?.upcomingTimeoffs) || has(dashboard?.upcomingHolidays)) && (
      <UpcomingTimeoffsTile rows={dashboard!.upcomingTimeoffs} holidays={dashboard!.upcomingHolidays || []} />
    ),
  ].filter(Boolean);

  const isLoading = dashboard === undefined;
  const kpiTiles = isLoading
    ? Array.from({ length: KPI_SKELETON_COUNT }).map((_, index) => <KpiTileSkeleton key={`kpi-skeleton-${index}`} />)
    : kpis();

  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {kpiTiles.length > 0 && (
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${kpiTiles.length}, minmax(0, 1fr))`,
            gap: 12,
          }}
        >
          {kpiTiles}
        </Box>
      )}

      {RoleUtils.isAdminOrManager() ? (
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            alignItems: "stretch",
          }}
        >
          <QuickLogTile onWorklogCreated={loadContent} />
          <Box style={{ position: "relative", minHeight: 240 }}>
            <Box style={{ position: "absolute", inset: 0 }}>
              <WorklogsApprovalTile onApprovalChange={loadContent} />
            </Box>
          </Box>
        </Box>
      ) : (
        <QuickLogTile onWorklogCreated={loadContent} />
      )}

      {isLoading && (
        <Box style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
          {Array.from({ length: PANEL_SKELETON_COUNT }).map((_, index) => (
            <PanelTileSkeleton key={`panel-skeleton-${index}`} />
          ))}
        </Box>
      )}

      {dashboard && areActivitiesToShow(dashboard) && panels.length > 0 && (
        <Box style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
          {panels.map((panel, index) => (
            <Box key={index} sx={{ display: "flex", width: "100%", "& > *": { flex: 1, minWidth: 0 } }}>
              {panel}
            </Box>
          ))}
        </Box>
      )}

      {areSitesToShow() && (
        <Box display={"flex"} flexDirection={"row"}>
          <SitesTile />
        </Box>
      )}

      {emptyDashboard() && <EmptyDashboard />}
    </Box>
  );
}
